// Image optimisation helper.
//
// Tenant hero/card images are arbitrary remote URLs (often multi-MB WordPress
// PNGs). We route them through a Cloudflare image-resizing Worker
// (img.vertu.co.za) that resizes to the display width and negotiates AVIF/WebP
// by Accept header, so the widget doesn't ship 2.8 MB PNGs into 300px cards.
//
// The proxy base is injected at build time (VITE_IMAGE_PROXY_BASE). When it is
// UNSET — local dev, or before the Worker is deployed — every function passes
// through the raw URL unchanged, so the widget keeps working (just without
// resize/format negotiation). The <img loading="lazy"> wins are independent of
// the proxy and land regardless.

const PROXY_BASE = (import.meta.env.VITE_IMAGE_PROXY_BASE as string | undefined)?.replace(/\/+$/, "");

// DPR-2 target widths per image role. Keep these lists small — each distinct
// (url,width) is a billed Cloudflare transform. (The resizer itself makes
// only two widget versions, 640 and 1280 wide; every width maps to one.)
//
// A header or gallery picture spans the content width, so its <img sizes> is
// "100vw" — a stale "520px" hint made browsers on ordinary screens take the
// 640 version and stretch it to ~1100px: blurry event headers (2 Oct).
export const IMG_WIDTHS = {
  HERO: [640, 960, 1280],
  CARD: [400, 600, 800],
  GALLERY_MAIN: [800, 1200],
  THUMB: [120, 200],
  LOGO: [88, 240],
  BANNER_FIXED: [96, 192],
  SEATING_TILE: [400, 600],
  SEATING_LIGHTBOX: [1000],
} as const;

type ImgOpts = {
  width?: number;
  quality?: number;
  format?: "auto" | "avif" | "webp";
  fit?: "cover" | "contain";
};

// True when a URL must never be sent to the resizer: absent, inline data/blob
// (upload previews), SVG (Cloudflare can't safely raster-resize vector logos),
// relative/same-origin, or already proxied.
function isPassthrough(src: string): boolean {
  if (!PROXY_BASE) return true;
  if (/^(data:|blob:)/i.test(src)) return true;
  if (!/^https?:\/\//i.test(src)) return true; // relative / same-origin
  if (/\.svg(\?|#|$)/i.test(src)) return true;
  if (src.startsWith(PROXY_BASE)) return true; // idempotent
  return false;
}

// Two backends, selected by the configured host so call-sites never change:
//   • Vertu Cloudflare Worker (img.vertu.co.za) — robust, Vertu-owned,
//     format=auto (AVIF→WebP→original by Accept). The intended production path.
//   • images.weserv.nl — zero-infra interim so compression can go live before
//     the Worker is deployed; set VITE_IMAGE_PROXY_BASE=https://images.weserv.nl.
//     Third-party public service — treat as temporary for a commercial path.
const IS_WESERV = !!PROXY_BASE && /(^|\.)images\.weserv\.nl$/i.test(
  (() => { try { return new URL(PROXY_BASE!).host; } catch { return ""; } })(),
);

/** Build a resized/format-negotiated URL for a single image. */
export function optimizedImageUrl(
  src: string | null | undefined,
  opts: ImgOpts = {},
): string | undefined {
  if (!src) return undefined;
  if (isPassthrough(src)) return src;
  const width = String(opts.width ?? 1280);
  const quality = String(opts.quality ?? 72);
  const fit = opts.fit ?? "cover";
  if (IS_WESERV) {
    // weserv can't Accept-negotiate; emit WebP (near-universal) unless a
    // concrete format is asked for. `we` = never upscale past the source.
    const p = new URLSearchParams({
      url: src,
      w: width,
      q: quality,
      output: opts.format && opts.format !== "auto" ? opts.format : "webp",
      fit,
      we: "",
    });
    return `${PROXY_BASE}/?${p.toString()}`;
  }
  const p = new URLSearchParams({
    url: src,
    w: width,
    q: quality,
    f: opts.format ?? "auto",
    fit,
  });
  return `${PROXY_BASE}/?${p.toString()}`;
}

/**
 * Build a `srcset` string across the given widths for <img srcset>. Returns
 * undefined in passthrough mode (no proxy) so the <img> just uses `src` — we
 * don't emit a srcset of identical raw URLs.
 */
export function optimizedSrcSet(
  src: string | null | undefined,
  widths: readonly number[],
  opts: Omit<ImgOpts, "width"> = {},
): string | undefined {
  if (!src || isPassthrough(src)) return undefined;
  return widths
    .map((w) => `${optimizedImageUrl(src, { ...opts, width: w })} ${w}w`)
    .join(", ");
}
